import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import BookingForm from "@/components/BookingForm";
import { SERVICE_TYPES, VEHICLE_OPTIONS } from "@/lib/bookingConfig";

// Map widget values to service-specific detail keys
function buildPrefill(service, params) {
  const pickup = params.pickup || "";
  const destination = params.destination || "";
  const date = params.date || "";
  const time = params.time || "";
  const passengers = params.passengers ? Number(params.passengers) : null;
  const luggage = params.luggage !== undefined && params.luggage !== "" ? Number(params.luggage) : null;
  const details = {};
  let transfer_type = "";

  if (date) details.pickup_date = date;
  if (time) details.pickup_time = time;
  if (passengers && !Number.isNaN(passengers)) details.passengers = passengers;
  if (luggage !== null && !Number.isNaN(luggage)) details.luggage_count = luggage;

  const looksLikeAirport = (s) =>
    /\b(airport|terminal|t1|t2|t3|syd|kingsford)\b/i.test(s || "");

  switch (service) {
    case "Airport Transfers": {
      if (looksLikeAirport(destination)) {
        transfer_type = "Departure";
        if (pickup) details.pickup_address = pickup;
        if (destination) details.destination_airport = destination;
      } else if (looksLikeAirport(pickup)) {
        transfer_type = "Arrival";
        if (pickup) details.arrival_airport = pickup;
        if (destination) details.dropoff_address = destination;
      } else {
        if (pickup) details.pickup_address = pickup;
      }
      break;
    }
    case "Corporate Travel":
    case "Cruise Transfers":
      if (pickup) details.pickup_location = pickup;
      if (destination) details.destination = destination;
      break;
    case "Hourly Hire":
    case "Wedding Transfers":
    case "Special Events":
      if (pickup) details.pickup_location = pickup;
      break;
    default:
      break;
  }
  return { details, transfer_type };
}

export default function BookPage() {
  const [searchParams] = useSearchParams();

  const initial = useMemo(() => {
    const params = Object.fromEntries(searchParams.entries());
    const service = SERVICE_TYPES.includes(params.service)
      ? params.service
      : "Airport Transfers";
    const mode = params.mode === "quote" ? "quote" : "booking";
    const vehicle = VEHICLE_OPTIONS.includes(params.vehicle) ? params.vehicle : null;
    const { details, transfer_type } = buildPrefill(service, params);
    return { service, mode, details, transfer_type, vehicle };
  }, [searchParams]);

  return (
    <div data-testid="book-page" className="min-h-screen bg-ivory">
      <Navbar />
      <section
        data-testid="book-page-header"
        className="bg-midnight text-warm-white pt-32 md:pt-40 pb-16 md:pb-20 border-b border-white/10"
      >
        <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
          <p className="sjc-eyebrow text-gold flex items-center gap-4">
            <span className="sjc-hairline w-10" />
            {initial.mode === "quote" ? "Quote Request" : "Reservation"}
          </p>
          <h1
            data-testid="page-header-title"
            className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] leading-[1.02] tracking-[-0.015em] max-w-4xl"
          >
            {initial.mode === "quote" ? (
              <>
                Tell us about <span className="italic font-light">your trip.</span>
              </>
            ) : (
              <>
                A few quiet details,{" "}
                <span className="italic font-light">and we&apos;ll take it from there.</span>
              </>
            )}
          </h1>
          <p className="mt-6 text-warm-white/65 text-base md:text-lg font-light leading-relaxed max-w-xl">
            Fields adapt to the service you&apos;ve selected — we&apos;ll only ask for what we need.
          </p>
        </div>
      </section>
      <main>
        <BookingForm
          initialService={initial.service}
          initialMode={initial.mode}
          initialDetails={initial.details}
          initialTransferType={initial.transfer_type}
          initialVehicle={initial.vehicle}
        />
      </main>
      <Footer />
      <FloatingActions />
    </div>
  );
}
